import React from 'react';

const PaymentMethodSelector = ({ selectedMethod, onMethodChange, paymentConfig, error }) => {
  if (!paymentConfig) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>
        Cargando métodos de pago...
      </div>
    );
  }

  const methods = [];

  // Agregar efectivo si está habilitado
  if (paymentConfig.efectivo?.enabled) {
    methods.push({
      value: 'efectivo',
      label: 'Pago en Efectivo',
      icon: '💵',
      description: paymentConfig.efectivo.instructions || 'El pago se coordinará al momento de la entrega'
    });
  }

  // Agregar transferencia si está habilitado
  if (paymentConfig.transferencia?.enabled) {
    methods.push({
      value: 'transferencia',
      label: 'Transferencia Bancaria',
      icon: '🏦',
      description: 'Transfiere a nuestra cuenta y sube el comprobante'
    });
  }

  if (methods.length === 0) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '8px',
        color: '#dc2626'
      }}>
        <h3 style={{ margin: '0 0 0.5rem 0' }}>⚠️ Métodos de pago no disponibles</h3>
        <p style={{ margin: 0, fontSize: '0.875rem' }}>
          No hay métodos de pago configurados en este momento.
        </p>
      </div>
    );
  }

  const formatCVU = (cvu) => {
    if (!cvu || cvu.length !== 22) return cvu;
    // Format as XXXX-XXXX-XXXX-XXXX-XXXXXX
    return cvu.replace(/(\d{4})(?=\d)/g, '$1-');
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // Simple feedback - you could enhance this with a toast notification
      const button = document.activeElement;
      const originalText = button.textContent;
      button.textContent = '¡Copiado!';
      button.style.backgroundColor = '#059669';
      setTimeout(() => {
        button.textContent = originalText;
        button.style.backgroundColor = '';
      }, 2000);
    } catch (err) {
      // Fallback for older browsers
      console.warn('Could not copy to clipboard:', err);
    }
  };

  return (
    <div style={{ marginBottom: '2rem' }}>
      <h3 style={{ marginBottom: '1rem', color: '#374151' }}>Método de Pago</h3>

      {error && (
        <div style={{
          padding: '0.75rem',
          marginBottom: '1rem',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          color: '#dc2626',
          fontSize: '0.875rem'
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gap: '1rem' }}>
        {methods.map((method) => (
          <div key={method.value} style={{ position: 'relative' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '1rem',
                padding: '1.25rem',
                border: `2px solid ${selectedMethod === method.value ? '#2563eb' : '#e5e7eb'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: selectedMethod === method.value ? '#eff6ff' : '#ffffff',
                transition: 'all 0.2s ease'
              }}
            >
              <input
                type="radio"
                name="paymentMethod"
                value={method.value}
                checked={selectedMethod === method.value}
                onChange={(e) => onMethodChange(e.target.value)}
                style={{
                  marginTop: '0.125rem',
                  transform: 'scale(1.2)'
                }}
              />

              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '1.25rem' }}>{method.icon}</span>
                  <span style={{ fontWeight: '600', color: '#374151' }}>{method.label}</span>
                </div>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                  {method.description}
                </p>
              </div>
            </label>

            {/* Mostrar detalles bancarios para transferencia */}
            {selectedMethod === 'transferencia' && method.value === 'transferencia' && (
              <div style={{
                marginTop: '1rem',
                padding: '1.5rem',
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#374151', fontSize: '1rem' }}>
                  📋 Datos para la transferencia
                </h4>

                <div style={{ display: 'grid', gap: '1rem' }}>
                  {paymentConfig.transferencia.alias && (
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                        Alias
                      </label>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem',
                        backgroundColor: '#ffffff',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontFamily: 'monospace',
                        fontSize: '1rem'
                      }}>
                        <span style={{ flex: 1 }}>{paymentConfig.transferencia.alias}</span>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(paymentConfig.transferencia.alias)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.75rem',
                            backgroundColor: '#f3f4f6',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          Copiar
                        </button>
                      </div>
                    </div>
                  )}

                  {paymentConfig.transferencia.cbuCvu && (
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                        CVU/CBU
                      </label>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem',
                        backgroundColor: '#ffffff',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontFamily: 'monospace',
                        fontSize: '1rem'
                      }}>
                        <span style={{ flex: 1 }}>{formatCVU(paymentConfig.transferencia.cbuCvu)}</span>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(paymentConfig.transferencia.cbuCvu)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.75rem',
                            backgroundColor: '#f3f4f6',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          Copiar
                        </button>
                      </div>
                    </div>
                  )}

                  {paymentConfig.transferencia.accountHolderName && (
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                        Titular
                      </label>
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: '#ffffff',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px'
                      }}>
                        {paymentConfig.transferencia.accountHolderName}
                      </div>
                    </div>
                  )}

                  {paymentConfig.transferencia.bankName && (
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                        Banco/Billetera
                      </label>
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: '#ffffff',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px'
                      }}>
                        {paymentConfig.transferencia.bankName}
                      </div>
                    </div>
                  )}
                </div>

                {paymentConfig.transferencia.instructions && (
                  <div style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    backgroundColor: '#fffbeb',
                    border: '1px solid #fbbf24',
                    borderRadius: '6px'
                  }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#92400e', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                      ⚠️ Instrucciones importantes
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#92400e' }}>
                      {paymentConfig.transferencia.instructions}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Mostrar información para efectivo */}
            {selectedMethod === 'efectivo' && method.value === 'efectivo' && (
              <div style={{
                marginTop: '1rem',
                padding: '1.5rem',
                backgroundColor: '#f0f9ff',
                border: '1px solid #0ea5e9',
                borderRadius: '8px'
              }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#0c4a6e', fontSize: '1rem' }}>
                  💵 Información del pago en efectivo
                </h4>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#0c4a6e' }}>
                  {paymentConfig.efectivo.instructions}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaymentMethodSelector;